import React, { useCallback, useState } from 'react';
import { Box, Modal } from '@mui/material';
import ForecastFeedbackPrompt from 'components/ForecastFeedbackPrompt';

import {
  getWindow,
  getWavetrakIdentity,
  trackEvent,
  trackNavigatedToPage,
} from '@surfline/web-common';
import ForecastFeedbackSnoozeModal from 'components/ForecastFeedbackSnoozeModal/ForecastFeedbackSnoozeModal';
import { useForecastFeedbackModalOpenState } from 'utils/forecastFeedbackState';
import ForecastFeedbackForms from 'components/ForecastFeedbackForms';
import classNames from 'classnames';

import styles from './ForecastFeedbackContainer.module.scss';

interface ForecastFeedbackContainerProps {
  className?: string;
  isPromptVisible: boolean;
  isSnoozePromptVisible: boolean;
  spotId: string;
  spotName: string;
  timezone: string;
  utcOffset: number;
  updateForecastFeedbackUserSetting: (...args: any) => void;
}

const ForecastFeedbackContainer: React.FC<ForecastFeedbackContainerProps> = ({
  className,
  isPromptVisible,
  isSnoozePromptVisible,
  spotId,
  spotName,
  timezone,
  utcOffset,
  updateForecastFeedbackUserSetting,
}) => {
  const [isModalOpen, setIsModalOpen] = useForecastFeedbackModalOpenState();
  const [isAccurateFeedback, setIsAccurateFeedback] = useState(false);

  const onHidePrompt = useCallback(() => {
    updateForecastFeedbackUserSetting('HIDE');
    trackEvent('Forecast Feedback Dismissed', {
      spotId,
      spotName,
    });
  }, [spotId, spotName, updateForecastFeedbackUserSetting]);

  const onSubmit = useCallback(() => {
    updateForecastFeedbackUserSetting('SUBMIT');
  }, [updateForecastFeedbackUserSetting]);

  const onSnoozeCancel = useCallback(() => {
    updateForecastFeedbackUserSetting('SNOOZE_CANCEL');
  }, [updateForecastFeedbackUserSetting]);

  const onSnoozeConfirm = useCallback(() => {
    updateForecastFeedbackUserSetting('SNOOZE_CONFIRM');

    const win = getWindow();
    const identity = getWavetrakIdentity();
    if (win?.analytics && win?.analytics?.identify && identity?.userId) {
      win.analytics.identify(identity.userId, { forecastFeedbackSnoozed: true });
    }
  }, [updateForecastFeedbackUserSetting]);

  const openFeedbackForm = useCallback(
    (isAccurate: boolean) => {
      trackEvent('Button Clicked', {
        buttonText: isAccurate ? 'Yes' : 'No',
        uiElement: 'Forecast feedback',
      });
      setIsAccurateFeedback(isAccurate);
      setIsModalOpen(true);
      trackNavigatedToPage('Forecast Feedback', {
        spotId,
        spotName,
      });
    },
    [setIsModalOpen, spotId, spotName],
  );

  return (
    <>
      {isPromptVisible && (
        <>
          <Box
            className={classNames(styles.forecastFeedback, className)}
            data-testid="forecast-feedback-component"
          >
            <ForecastFeedbackPrompt
              onHidePrompt={onHidePrompt}
              openFeedbackForm={openFeedbackForm}
            />
          </Box>
          <Modal open={isModalOpen} className={styles.modal}>
            <Box className={styles.modalContent}>
              <ForecastFeedbackForms
                isAccurateFeedback={isAccurateFeedback}
                onSubmit={onSubmit}
                spotId={spotId}
                spotName={spotName}
                timezone={timezone}
                utcOffset={utcOffset}
              />
            </Box>
          </Modal>
        </>
      )}
      {isSnoozePromptVisible && (
        <ForecastFeedbackSnoozeModal
          isModalOpen={isSnoozePromptVisible}
          onSnoozeCancel={onSnoozeCancel}
          onSnoozeConfirm={onSnoozeConfirm}
        />
      )}
    </>
  );
};

export default ForecastFeedbackContainer;
